import {
  type AccountTransactionChildWithDerivedStatuses,
  type AccountTransaction,
  ContractInteractionChildWithContext,
} from '@mls-wallet/shared-types';
import { format, isValid } from 'date-fns';

/**
 * Get display name for a contract address.
 * Uses the contract name from block explorer if available, otherwise returns "Contract".
 */
export function getContractDisplayName(
  transaction: ContractInteractionChildWithContext
): string {
  if (transaction.contractAddressName) {
    return transaction.contractAddressName;
  }
  if (transaction.contractName) {
    return transaction.contractName;
  }
  // Fallback to generic "Contract" if name not available
  return 'Contract';
}

/**
 * Resolve the most useful identity for a contract call that has no decoded
 * function metadata. Unlike the decoded-call fallback above, opaque calls use
 * the destination address so the UI never collapses to an ambiguous
 * "Contract" label.
 */
export function getOpaqueContractDisplayName(
  transaction: ContractInteractionChildWithContext
): string {
  return (
    transaction.contractAddressName?.trim() ||
    transaction.contractName?.trim() ||
    transaction.toAddress
  );
}

export type TransactionExpiryTone = 'default' | 'warning';

type TransactionWithExpiration = Pick<
  AccountTransactionChildWithDerivedStatuses,
  'expirationTimestamp'
>;

type HistoricSortableTransaction = Pick<
  AccountTransactionChildWithDerivedStatuses,
  'dateExecuted' | 'expirationTimestamp'
>;

type StoredTransactionSimulationCandidate = Pick<
  AccountTransactionChildWithDerivedStatuses,
  'executionStatus' | 'invalidated' | 'signatureStatus'
>;

/**
 * Resolve a selected row against the latest polled transaction collections.
 * Historic wins during a short feed overlap because it carries the terminal
 * status that moved the transaction out of the queue. Queued selections stop
 * resolving as soon as the page's live clock reaches their expiry boundary.
 */
export function resolveSelectedTransaction<
  T extends AccountTransactionChildWithDerivedStatuses | AccountTransaction,
>(
  transactionId: string | null,
  queuedTransactions: readonly T[],
  historicTransactions: readonly T[],
  nowMs: number
): T | null {
  if (transactionId === null) {
    return null;
  }

  const historicTransaction = historicTransactions.find(
    ({ id }) => id === transactionId
  );
  if (historicTransaction) {
    return historicTransaction;
  }

  const queuedTransaction = queuedTransactions.find(
    ({ id }) => id === transactionId
  );
  return queuedTransaction && !isTransactionExpired(queuedTransaction, nowMs)
    ? queuedTransaction
    : null;
}

/**
 * Keep previews available while a transaction has no final execution outcome.
 * A blocked attempt remains previewable because simulation supplies its own
 * temporary signatures and does not depend on collected reviewer decisions.
 */
export function canSimulateStoredTransaction(
  transaction: StoredTransactionSimulationCandidate
): boolean {
  if (transaction.invalidated || transaction.signatureStatus === 'expired') {
    return false;
  }

  return (
    transaction.executionStatus === null ||
    transaction.executionStatus === 'blocked'
  );
}

function getRemainingTransactionLifetimeSeconds(
  expirationTimestamp: number,
  nowMs: number
): number {
  return expirationTimestamp - Math.floor(nowMs / 1000);
}

function formatCountdownUnit(value: number, singular: string): string {
  return `${value} ${singular}${value === 1 ? '' : 's'}`;
}

function formatSubDayCountdown(remainingSeconds: number): string {
  if (remainingSeconds < 60) {
    return formatCountdownUnit(remainingSeconds, 'second');
  }

  if (remainingSeconds < 60 * 60) {
    return formatCountdownUnit(Math.ceil(remainingSeconds / 60), 'minute');
  }

  return formatCountdownUnit(Math.ceil(remainingSeconds / (60 * 60)), 'hour');
}

export function isTransactionExpired(
  transaction: TransactionWithExpiration,
  nowMs: number = Date.now()
): boolean {
  return (
    getRemainingTransactionLifetimeSeconds(
      transaction.expirationTimestamp,
      nowMs
    ) <= 0
  );
}

export function getTransactionExpiryDisplay(
  expirationTimestamp: number,
  nowMs: number = Date.now()
): {
  label: string;
  tone: TransactionExpiryTone;
} | null {
  const remainingSeconds = getRemainingTransactionLifetimeSeconds(
    expirationTimestamp,
    nowMs
  );

  if (remainingSeconds <= 0) {
    return null;
  }

  if (remainingSeconds < 24 * 60 * 60) {
    return {
      label: formatSubDayCountdown(remainingSeconds),
      tone: 'warning',
    };
  }

  return {
    label: formatCountdownUnit(
      Math.ceil(remainingSeconds / (24 * 60 * 60)),
      'day'
    ),
    tone: 'default',
  };
}

export function getHistoricTransactionSortTimestamp(
  transaction: HistoricSortableTransaction
): number {
  // Historic rows should sort by the moment they actually became history:
  // executed rows use dateExecuted, while expired pending rows fall back to
  // their expiration time because they never receive an execution timestamp.
  if (transaction.dateExecuted) {
    const executedAt = new Date(transaction.dateExecuted);

    if (isValid(executedAt)) {
      return executedAt.getTime();
    }
  }

  return transaction.expirationTimestamp * 1000;
}

function formatTransactionDateValue(
  value: Date | number | string,
  formatString: string
): string {
  const date = value instanceof Date ? value : new Date(value);

  if (!isValid(date)) {
    return '--';
  }

  return format(date, formatString);
}

export function formatTransactionDate(value: Date | number | string): string {
  return formatTransactionDateValue(value, 'MM/dd/yyyy');
}

export function formatTransactionDateTime(
  value: Date | number | string
): string {
  return formatTransactionDateValue(value, 'MM/dd/yyyy, hh:mm a');
}

export function formatTransactionExpirationTimestamp(
  expirationTimestamp: number
): string {
  return formatTransactionDateTime(expirationTimestamp * 1000);
}
